// frontend/src/pages/Admin/Usuarios.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import '../../styles/Admin/Usuarios.css';

const Usuarios = () => {
  const { user: currentUser } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [instituciones, setInstituciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [usuarioEditando, setUsuarioEditando] = useState(null);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [filtros, setFiltros] = useState({
    rol: 'todos',
    institucion: 'todos',
    activo: 'todos',
    busqueda: ''
  });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nombre: '',
    role: 'consultor_1',
    institucionId: ''
  });
  const [passwordData, setPasswordData] = useState({
    passwordNueva: '',
    confirmPassword: ''
  });

  const roles = [
    { value: 'super_admin_jasp', label: 'Super Administrador', nivel: 'Alto' },
    { value: 'admin_jasp', label: 'Administrador JASP', nivel: 'Alto' },
    { value: 'consultor_1', label: 'Consultor Nivel 1', nivel: 'Medio' },
    { value: 'consultor_2', label: 'Consultor Nivel 2', nivel: 'Medio' },
    { value: 'consultor_3', label: 'Consultor Nivel 3', nivel: 'Medio' },
    { value: 'institucion', label: 'Institución', nivel: 'Básico' }
  ];

  useEffect(() => {
    cargarUsuarios();
    cargarInstituciones();
  }, [filtros]);

  const cargarUsuarios = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtros.rol !== 'todos') params.append('rol', filtros.rol);
      if (filtros.institucion !== 'todos') params.append('institucion', filtros.institucion);
      if (filtros.activo !== 'todos') params.append('activo', filtros.activo);
      if (filtros.busqueda) params.append('busqueda', filtros.busqueda);

      const response = await api.get(`/usuarios?${params.toString()}`);
      setUsuarios(response.data.data);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarInstituciones = async () => {
    try {
      const response = await api.get('/instituciones');
      setInstituciones(response.data.data || []);
    } catch (error) {
      console.error('Error al cargar instituciones:', error);
    }
  };

  const handleGuardarUsuario = async (e) => {
    e.preventDefault();
    
    try {
      if (usuarioEditando) {
        // Actualizar usuario existente
        await api.put(`/usuarios/${usuarioEditando._id}`, formData);
        alert('✅ Usuario actualizado correctamente');
      } else {
        // Crear nuevo usuario
        await api.post('/usuarios', formData);
        alert('✅ Usuario creado correctamente');
      }

      setShowModal(false);
      resetFormData();
      cargarUsuarios();
    } catch (error) {
      console.error('Error al guardar usuario:', error);
      alert(error.response?.data?.msg || 'Error al guardar el usuario');
    }
  };

  const handleCambiarPassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.passwordNueva !== passwordData.confirmPassword) {
      alert('❌ Las contraseñas no coinciden');
      return;
    }

    try {
      await api.put(`/usuarios/${usuarioSeleccionado._id}/cambiar-password`, {
        passwordNueva: passwordData.passwordNueva
      });
      
      alert('✅ Contraseña cambiada correctamente');
      setShowPasswordModal(false);
      setPasswordData({ passwordNueva: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      alert(error.response?.data?.msg || 'Error al cambiar la contraseña');
    }
  };

  const handleEliminarUsuario = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await api.delete(`/usuarios/${id}`);
      alert('✅ Usuario eliminado correctamente');
      cargarUsuarios();
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      alert(error.response?.data?.msg || 'Error al eliminar el usuario');
    }
  };

  const handleToggleActivo = async (usuario) => {
    try {
      await api.put(`/usuarios/${usuario._id}`, {
        ...usuario,
        activo: !usuario.activo
      });
      cargarUsuarios();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      alert('Error al cambiar el estado del usuario');
    }
  };

  const resetFormData = () => {
    setFormData({
      email: '',
      password: '',
      nombre: '',
      role: 'consultor_1',
      institucionId: ''
    });
    setUsuarioEditando(null);
  };

  const abrirModalNuevo = () => {
    resetFormData();
    setShowModal(true);
  };

  const abrirModalEditar = (usuario) => {
    setUsuarioEditando(usuario);
    setFormData({
      email: usuario.email,
      password: '', // No mostramos la contraseña
      nombre: usuario.nombre,
      role: usuario.role,
      institucionId: usuario.institucionId?._id || ''
    });
    setShowModal(true);
  };

  const getRoleBadge = (role) => {
    const rol = roles.find(r => r.value === role);
    return (
      <span className={`role-badge ${role}`}>
        {rol?.label || role}
      </span>
    );
  };

  const getEstadoBadge = (activo) => {
    return (
      <span className={`estado-badge ${activo ? 'activo' : 'inactivo'}`}>
        {activo ? 'Activo' : 'Inactivo'}
      </span>
    );
  };

  return (
    <div className="usuarios-admin">
      <div className="header">
        <div>
          <h1>Gestión de Usuarios</h1>
          <p>Administra los usuarios del sistema JASP</p>
        </div>
        <button className="btn-primary" onClick={abrirModalNuevo}>
          + Nuevo Usuario
        </button>
      </div>

      {/* Filtros */}
      <div className="filtros-card">
        <div className="filtros-grid">
          <div className="filtro-group">
            <label>Buscar</label>
            <input
              type="text"
              placeholder="Nombre o email..."
              value={filtros.busqueda}
              onChange={(e) => setFiltros({...filtros, busqueda: e.target.value})}
              className="form-control"
            />
          </div>

          <div className="filtro-group">
            <label>Rol</label>
            <select
              value={filtros.rol}
              onChange={(e) => setFiltros({...filtros, rol: e.target.value})}
              className="form-control"
            >
              <option value="todos">Todos los roles</option>
              {roles.map(rol => (
                <option key={rol.value} value={rol.value}>{rol.label}</option>
              ))}
            </select>
          </div>

          <div className="filtro-group">
            <label>Institución</label>
            <select
              value={filtros.institucion}
              onChange={(e) => setFiltros({...filtros, institucion: e.target.value})}
              className="form-control"
            >
              <option value="todos">Todas las instituciones</option>
              {instituciones.map(inst => (
                <option key={inst._id} value={inst._id}>{inst.nombre}</option>
              ))}
            </select>
          </div>

          <div className="filtro-group">
            <label>Estado</label>
            <select
              value={filtros.activo}
              onChange={(e) => setFiltros({...filtros, activo: e.target.value})}
              className="form-control"
            >
              <option value="todos">Todos</option>
              <option value="activos">Activos</option>
              <option value="inactivos">Inactivos</option>
            </select>
          </div>

          <div className="filtro-actions">
            <button 
              className="btn-secondary"
              onClick={() => setFiltros({
                rol: 'todos',
                institucion: 'todos',
                activo: 'todos',
                busqueda: ''
              })}
            >
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabla de usuarios */}
      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Rol</th>
              <th>Institución</th>
              <th>Estado</th>
              <th>Registro</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center">Cargando usuarios...</td>
              </tr>
            ) : usuarios.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">No hay usuarios para mostrar</td>
              </tr>
            ) : (
              usuarios.map(usuario => (
                <tr key={usuario._id}>
                  <td>
                    <div className="usuario-info">
                      <div className="usuario-avatar">
                        {usuario.nombre.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="usuario-nombre">{usuario.nombre}</div>
                        <div className="usuario-email">{usuario.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>{getRoleBadge(usuario.role)}</td>
                  <td>
                    {usuario.institucionId?.nombre || (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>{getEstadoBadge(usuario.activo)}</td>
                  <td>
                    {new Date(usuario.fechaRegistro).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="acciones">
                      <button
                        className="btn-icon"
                        onClick={() => abrirModalEditar(usuario)}
                        title="Editar"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => {
                          setUsuarioSeleccionado(usuario);
                          setShowPasswordModal(true);
                        }}
                        title="Cambiar contraseña"
                      >
                        🔑
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleToggleActivo(usuario)}
                        title={usuario.activo ? 'Desactivar' : 'Activar'}
                      >
                        {usuario.activo ? '🔴' : '🟢'}
                      </button>
                      {currentUser?.role === 'super_admin_jasp' && 
                       usuario.role !== 'super_admin_jasp' && (
                        <button
                          className="btn-icon delete"
                          onClick={() => handleEliminarUsuario(usuario._id)}
                          title="Eliminar"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal para crear/editar usuario */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-lg">
            <h2>{usuarioEditando ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
            
            <form onSubmit={handleGuardarUsuario}>
              <div className="form-group">
                <label>Nombre completo *</label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="form-control"
                  required
                  disabled={usuarioEditando}
                />
              </div>

              {!usuarioEditando && (
                <div className="form-group">
                  <label>Contraseña *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="form-control"
                    required={!usuarioEditando}
                    minLength="6"
                  />
                  <small className="text-muted">Mínimo 6 caracteres</small>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Rol *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    className="form-control"
                    required
                  >
                    {roles.map(rol => (
                      <option key={rol.value} value={rol.value}>{rol.label}</option>
                    ))}
                  </select>
                </div>

                {formData.role === 'institucion' && (
                  <div className="form-group">
                    <label>Institución *</label>
                    <select
                      value={formData.institucionId}
                      onChange={(e) => setFormData({...formData, institucionId: e.target.value})}
                      className="form-control"
                      required={formData.role === 'institucion'}
                    >
                      <option value="">Selecciona una institución</option>
                      {instituciones.map(inst => (
                        <option key={inst._id} value={inst._id}>{inst.nombre}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {usuarioEditando ? 'Actualizar' : 'Crear'} Usuario
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para cambiar contraseña */}
      {showPasswordModal && usuarioSeleccionado && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Cambiar Contraseña</h2>
            <p className="modal-subtitle">
              Usuario: <strong>{usuarioSeleccionado.nombre}</strong>
            </p>

            <form onSubmit={handleCambiarPassword}>
              <div className="form-group">
                <label>Nueva contraseña *</label>
                <input
                  type="password"
                  value={passwordData.passwordNueva}
                  onChange={(e) => setPasswordData({...passwordData, passwordNueva: e.target.value})}
                  className="form-control"
                  required
                  minLength="6"
                />
              </div>

              <div className="form-group">
                <label>Confirmar contraseña *</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  className="form-control"
                  required
                  minLength="6"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ passwordNueva: '', confirmPassword: '' });
                }}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Cambiar Contraseña
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Usuarios;